import { createContext, useContext, useEffect, useState } from 'react';
import { cloneDeep, set, get, last, isNil, isArray, isNumber, omit, isEmpty } from 'lodash';
import * as Yup from 'yup';
import { ValidateOptions } from 'yup/lib/types';
import errorHandler from 'Utils/error-handler';
import { useTranslation } from 'react-i18next';
import { AllPathsOfObject, IDictionary } from '@life-line-lab/components-react';

export interface GenericEventWithTargetValue {
  target: {
    value?: any;
    checked?: any;
    [k: string | number | symbol]: any;
  },
  [k: string | number | symbol]: any;
}

export interface UseFormType<T extends IDictionary> {
  initialValues: T;
  yupSchema?: Yup.BaseSchema;
  /**
   * Any extra yup validate options, default will be example below.
   * 
   * @example
   * {
   *    strict: true,
   *    abortEarly: true,
   *    stripUnknown: false,
   *    context: { values: values },
   * }
   */
  yupValidateOptions?: ValidateOptions<any>;
  /**
   * Optional.
   * Normalize the input values and returns the normalized values.
   */
  normalizeOnValuesBeforeValidation?: (values: T) => any;
  /**
   * Optional.
   * To do some custom validation before Yup.
   * @returns **true** if validation success (continue Yup validation, etc.), false otherwise.
   */
  additionalValidationBeforeYup?: (values: T) => boolean;
}

export interface GetCommonPropsByNameReturnType {
  value: any;
  name: string;
  onChange: (valueOrEvent: string | string[] | React.ChangeEvent<HTMLInputElement> | GenericEventWithTargetValue) => void;
  isError: boolean;
  label: string;
  error: string;
}

export interface FormSubmitState {
  /**
   * Whether the user confirm to submit, use this flag to trigger API request to prevent duplicate submission
   */
  isSubmitting: boolean;
  /**
   * Values that are normalized before yup validation, a normal object.
   * Use this if you need some pre-normalization that is also done before Yup validation.
   */
  normalizedValues?: any;
  /**
   * The validated and cleaned values object that is returned from Yup validation
   * Use this if you need some post-validation values.
   */
  validatedValues?: any;
}

export type GetCommonPropsByNameType<T> = (
  name: keyof T | AllPathsOfObject<T>,
  except?: (keyof GetCommonPropsByNameReturnType)[],
) => GetCommonPropsByNameReturnType;

export type HandleInputChangeCallbackType<T> = (
  name: keyof T | AllPathsOfObject<T>,
) => (valueOrEvent: string | string[] | React.ChangeEvent<HTMLInputElement> | GenericEventWithTargetValue) => void;

type UseLLLFormReturnType<T extends IDictionary = any> = {
  values: T;
  setValues: React.Dispatch<React.SetStateAction<T>>;
  errors: any;
  setErrors: React.Dispatch<any>;
  handleInputChangeCallback: (name: keyof T | AllPathsOfObject<T>) => (valueOrEvent: string | string[] | React.ChangeEvent<HTMLInputElement> | GenericEventWithTargetValue) => void;
  onSubmit: () => void;
  onCancelSubmit: () => void;
  formSubmitState: FormSubmitState;
  getCommonPropsByName: (name: keyof T | AllPathsOfObject<T>, except?: (keyof GetCommonPropsByNameReturnType)[]) => GetCommonPropsByNameReturnType;
}
const LLLFormContext = createContext<UseLLLFormReturnType | null>(null)
export const LLLFormProvider = ({ children, data }: { children: React.ReactNode, data: UseLLLFormReturnType}) => 
  <LLLFormContext.Provider value={data as unknown as UseLLLFormReturnType}>
    {children}
  </LLLFormContext.Provider>
export const useLLLFormContext: <T extends IDictionary>() => UseLLLFormReturnType<T> = <T extends IDictionary>() => useContext(LLLFormContext) as any


function useLLLForm<T extends IDictionary>({
  initialValues,
  yupSchema,
  normalizeOnValuesBeforeValidation,
  additionalValidationBeforeYup,
  yupValidateOptions,
}: UseFormType<T>) {
  const { t } = useTranslation();
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<any>({});
  const [formSubmitState, setFormSubmitState] = useState<FormSubmitState>({
    isSubmitting: false,
  });

  const handleInputChangeCallback =
    (name: keyof T | AllPathsOfObject<T>) =>
    (valueOrEvent: string | string[] | React.ChangeEvent<HTMLInputElement> | GenericEventWithTargetValue) => {
      let value: string | string[] | boolean = valueOrEvent as string | string[];
      const castEvent = valueOrEvent as React.ChangeEvent<HTMLInputElement>;
      if (!isNil(castEvent?.target?.value)) {
        // * note: can be empty string
        value = castEvent.target.value;
      } else if (!isNil(castEvent?.target?.checked)) {
        value = castEvent.target.checked;
      }
      setValues((prevState) => {
        const clonedPrevState = cloneDeep(prevState);
        set(clonedPrevState, name, value);
        return clonedPrevState;
      });
    };

  const getCommonPropsByName = (
    name: keyof T | AllPathsOfObject<T>,
    except?: (keyof GetCommonPropsByNameReturnType)[],
  ): GetCommonPropsByNameReturnType => {
    let normalizedName = '';

    if (isArray(name)) {
      for (const [i, v] of name.entries()) {
        if (isNumber(v)) {
          normalizedName += `[${v}]`;
        } else {
          if (i !== 0) {
            normalizedName += '.';
          }
          normalizedName += String(v);
        }
      }
    } else {
      normalizedName = name as string;
    }

    const labelPath = last(normalizedName.split('.'))?.replace(/\[\d+\]/g, '') ?? '';
    const errorMessage = get(errors, normalizedName);
    
    let result: GetCommonPropsByNameReturnType = {
      value: get(values, name),
      name: normalizedName,
      onChange: handleInputChangeCallback(name),
      isError: !!errorMessage,
      label: t(`label.${labelPath}`),
      error: errorMessage,
    };

    if (except) {
      return omit(result, except) as GetCommonPropsByNameReturnType; // for simplicity
    }

    return result;
  };

  const onSubmit = () => {
    setErrors({});

    try {
      let normalizedValues: any = cloneDeep(values);
      if (normalizeOnValuesBeforeValidation) {
        normalizedValues = normalizeOnValuesBeforeValidation(normalizedValues);
      }

      if (additionalValidationBeforeYup) {
        const additionalValidationResult = additionalValidationBeforeYup(values);
        if (!additionalValidationResult) return;
      }

      if (!yupSchema) {
        setFormSubmitState({
          isSubmitting: true,
        });
        return;
      }
      const validatedValues = yupSchema.validateSync(normalizedValues, {
        strict: true,
        abortEarly: true,
        stripUnknown: false,
        context: { values },
        ...(yupValidateOptions ?? {}),
      });
      setFormSubmitState({
        isSubmitting: true,
        normalizedValues,
        validatedValues,
      });
    } catch (err: any) {
      setFormSubmitState({
        isSubmitting: false,
      });
      let { path: fieldName, value } = errorHandler.validation(err);

      let fieldLabelDisplayName: string = fieldName || '';
      if (fieldLabelDisplayName.includes('.')) {
        fieldLabelDisplayName = last(fieldLabelDisplayName.split('.')) || '';
      }

      setErrors(prevState => ({
        ...prevState,
        [fieldName]: t(`${value}`, { field: t(`label.${fieldLabelDisplayName}`) }),
      }));

      try {
        if (document.querySelector(`input[name='${fieldName}']`)) {
          (document.querySelector(`input[name='${fieldName}']`) as HTMLInputElement).focus();
        } else if (document.querySelector(`div[name='${fieldName}']`)) {
          (document.querySelector(`div[name='${fieldName}'] input`) as HTMLInputElement).focus();
        }
      } catch (err2) {}
    }
  };

  const onCancelSubmit = () => {
    setFormSubmitState({
      isSubmitting: false,
    });
  };

  /**
   * Clear error message when user make some changes, for better UX  
   */
  useEffect(() => {
    if (!isEmpty(errors)) {
      setErrors({})
    }
  }, [values])

  return {
    values,
    setValues,
    errors,
    setErrors,
    /**
     * This handler function can be used on both <SelectInput /> and <TextInput />, even though they have different function signature:
     * - <SelectInput /> => (value: string | string[]) => void;
     * - <TextInput /> => (e: React.ChangeEvent<HTMLInputElement>) => void;
     *
     * The function will check whether the value passed into it is event or string|string[], and act accordingly.
     *
     * @example
     * <TextInput
     *  ...
     *  name="fullName"
     *  handleChange={handleInputChangeCallback('fullName')}
     * />
     *
     * const orderIDField = `something[${currIndex}].orderID`;
     * // or, for strict typechecking
     * const orderIDField = ['something', currIndex, 'orderID'];
     * ...
     * <SelectInput
     *  values={_.get(values, orderIDField)}
     *  name={orderIDField}
     *  handleChange={handleSelectChangeCallback(orderIDField)}
     * />
     *
     * @param name Path of the property to be set, e.g. **"customer[1].fullName"**
     * @returns
     */
    handleInputChangeCallback,
    /**
     * To trigger validation, and if validation success, will set **isSubmitting** flag to true.
     *
     * @example
     * <Button
     *  ...
     *  onClick={onSubmit}
     * />
     */
    onSubmit,
    /**
     * Use this to cancel the form submission, so that user can submit again.
     *
     * Can be used for error handling from API request
     */
    onCancelSubmit,

    formSubmitState,

    /**
     *
     * @param name string or string[], that is the path of the input object
     *
     * @example
     * <TextInput
     *  {...getCommonPropsByName('fullName')}
     * />
     * <SelectInput
     *  {...getCommonPropsByName('orgID', ['label'])}
     * />
     */
    getCommonPropsByName,
  };
}

export default useLLLForm;
